import { useQuery } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type { DispatchWeekResponse } from './contractsMirror.js';
import type { DispatchSearch } from './searchParams.js';

/**
 * TanStack Query hook for the COARSE dispatch endpoint (the strangler-fig + coarse-endpoint proof,
 * CARVE-07). One query key per `{week}` — navigating to a new week drives exactly one refetch of
 * `GET /api/v1/dispatch?week=…`. The SERVER resolves the Monday-start week + aggregates the
 * assignments + the assignable jobs + categories + crew (22-11); the SPA only renders the returned
 * `DispatchWeekResponse` (no client re-aggregation, no new N+1 — threat T-22-tenant / CARVE-07).
 *
 * Every request goes through `apiFetch` (the only fetch path — `credentials:'include'` +
 * `X-Tenant-Subdomain` baked in, single-flight 401 refresh). The tenant is derived server-side from
 * the auth context; the SPA NEVER sends a tenant id param (threat T-22-tenant).
 *
 * Plan 22-23 — live polling: the coarse GET already returns each assignment's status, so a
 * `refetchInterval` keeps the board's statuses fresh as the field crew acknowledges / advances
 * their assignments (via the public status page) without the dispatcher navigating. Polling pauses
 * when the tab is hidden (default `refetchIntervalInBackground: false`).
 */

/** How often (ms) the board re-polls the week for live status updates. */
const DISPATCH_POLL_INTERVAL_MS = 30_000;

/** Build the stable query key for a week (one cache entry per selected week). */
export function dispatchWeekQueryKey(search: DispatchSearch): readonly unknown[] {
  return ['dispatch', search.week ?? null] as const;
}

/** Build the coarse-endpoint request path for the selected week (no tenant param). */
export function dispatchRequestPath(search: DispatchSearch): string {
  return search.week
    ? `/api/v1/dispatch?week=${encodeURIComponent(search.week)}`
    : '/api/v1/dispatch';
}

export function useDispatchWeekQuery(search: DispatchSearch) {
  return useQuery<DispatchWeekResponse>({
    queryKey: dispatchWeekQueryKey(search),
    queryFn: () => apiFetchJson<DispatchWeekResponse>(dispatchRequestPath(search)),
    refetchInterval: DISPATCH_POLL_INTERVAL_MS,
  });
}
